import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowRight, Mail } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNLOCK_KEY = "sisyphean_preview_unlocked_v1";

// Public paths that always bypass the coming-soon gate (users hitting these
// come from external emails or transactional flows and shouldn't be walled off).
const GATE_BYPASS_PATHS = ["/unsubscribe"];

export default function ComingSoonGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem(UNLOCK_KEY) === "true"; } catch { return false; }
  });

  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (unlocked || GATE_BYPASS_PATHS.some((p) => path.startsWith(p))) return children;
  return <GatePage onUnlock={() => {
    try { localStorage.setItem(UNLOCK_KEY, "true"); } catch (e) { /* ignore */ }
    setUnlocked(true);
  }} />;
}

function GatePage({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setPwLoading(true); setPwError("");
    try {
      await axios.post(`${API}/coming-soon/unlock`, { password: pw.trim() });
      onUnlock();
    } catch (err) {
      const msg = err?.response?.data?.detail || "That didn't work.";
      setPwError(msg);
      setPwLoading(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmailLoading(true);
    try {
      const r = await axios.post(`${API}/coming-soon/subscribe`, { email: email.trim() });
      setEmailSent(true);
      if (r.data?.already_subscribed) {
        toast.success("You're already on the list.");
      } else {
        toast.success("On the list. We'll be in touch.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't subscribe. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Couldn't subscribe.");
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden" data-testid="coming-soon-gate">
      {/* Subtle grid background, consistent with the rest of the site */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.07]" style={{
        backgroundImage: "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      <div className="relative max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-16 min-h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-12 sm:mb-20">
          <Logo size="md" />
          <span className="terminal-label text-muted-foreground hidden sm:inline">[ status: pre-launch ]</span>
        </div>

        {/* Hero */}
        <div className="flex-1 flex flex-col justify-center">
          <div className="terminal-label text-primary mb-4">[ COMING.SOON ]</div>
          <h1 className="font-display font-semibold tracking-tight leading-[0.95] text-4xl sm:text-6xl mb-6">
            Something for creatives the algorithm hasn&apos;t seen before.
          </h1>
          <p className="text-base sm:text-lg text-foreground/80 leading-relaxed max-w-xl">
            We&apos;re putting the finishing touches on <span className="font-medium text-foreground">Sisyphean</span> - an AI tool built to do something no other resume product can: rewrite your portfolio for every job, not just your resume.
          </p>
          <p className="text-sm sm:text-base text-muted-foreground mt-4 max-w-xl">
            If a recruiter&apos;s AI is going to read your work before a human ever clicks, the work should be talking back. That launches soon.
          </p>

          {/* Email capture */}
          <form onSubmit={submitEmail} className="mt-10 max-w-md" data-testid="coming-soon-email-form">
            <label className="terminal-label text-muted-foreground block mb-2">[ NOTIFY.ME.AT.LAUNCH ]</label>
            {emailSent ? (
              <div className="panel px-4 py-3 flex items-center gap-3 text-sm" data-testid="coming-soon-email-success">
                <span className="w-2 h-2 rounded-full" style={{ background: "hsl(var(--accent-green))" }} />
                <span>You&apos;re in. We&apos;ll send one email when the door opens.</span>
              </div>
            ) : (
              <div className="flex items-stretch gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="input-base w-full pl-9 text-sm"
                    data-testid="coming-soon-email-input"
                    autoComplete="email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailLoading}
                  className="btn-primary inline-flex items-center gap-2 text-xs whitespace-nowrap"
                  data-testid="coming-soon-email-submit"
                >
                  {emailLoading ? "Adding..." : <>Notify me <ArrowRight className="w-3 h-3" /></>}
                </button>
              </div>
            )}
            <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground/70 mt-2">
              One email, on launch day. No marketing churn.
            </p>
          </form>
        </div>

        {/* Footer with password unlock */}
        <div className="mt-12 pt-8 border-t border-foreground/15">
          <details className="group" data-testid="coming-soon-pw-details">
            <summary className="cursor-pointer list-none inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-mono text-muted-foreground hover:text-foreground transition-colors">
              <Lock className="w-3 h-3" />
              <span>I have a passcode</span>
            </summary>
            <form onSubmit={submitPassword} className="mt-4 max-w-md" data-testid="coming-soon-pw-form">
              <div className="flex items-stretch gap-2">
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => { setPw(e.target.value); setPwError(""); }}
                  placeholder="passcode"
                  className="input-base flex-1 text-sm font-mono"
                  data-testid="coming-soon-pw-input"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={pwLoading || !pw.trim()}
                  className="btn-secondary text-xs whitespace-nowrap"
                  data-testid="coming-soon-pw-submit"
                >
                  {pwLoading ? "Checking..." : "Enter"}
                </button>
              </div>
              {pwError && (
                <p className="text-[11px] text-primary mt-2 font-mono" data-testid="coming-soon-pw-error">{pwError}</p>
              )}
            </form>
          </details>
          <p className="text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground/60 mt-8">
            © Sisyphean · A creative-first job tool
          </p>
        </div>
      </div>
    </div>
  );
}
